import React, { useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Tag,
  Steps,
  Progress,
  Descriptions,
  Button,
  Space,
  Collapse,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import SubPageLayout from '../helpers/SubPageLayout';

const { Step } = Steps;
const { Panel } = Collapse;

// ---------- PRODUCTION STAGE STRUCTURE ----------

const PRODUCTION_STRUCTURE = [
  {
    key: 'Pre-build',
    name: 'Pre-build',
    sections: [
      {
        title: null,
        items: [
          { label: 'Pre-build sign offs' },
          { label: 'Build Sheet' },
          { label: 'Drawings' },
          { label: 'Fabrication sheet' },
          { label: 'Paint Sheet' },
          { label: 'Send to Fabrication' },
        ],
      },
    ],
  },
  {
    key: 'Fabrication',
    name: 'Fabrication',
    sections: [
      {
        title: null,
        items: [
          { label: 'Fabrication inspection' },
          { label: 'Fabrication Sign off' },
        ],
      },
    ],
  },
  {
    key: 'Paint & Blast',
    name: 'Paint & Blast',
    sections: [
      {
        title: null,
        items: [{ label: 'Paint Sign off' }],
      },
    ],
  },
  {
    key: 'Materials',
    name: 'Materials',
    sections: [
      {
        title: null,
        items: [{ label: 'Bill of Materials' }],
      },
    ],
  },
  {
    key: 'Stickers',
    name: 'Stickers',
    sections: [
      {
        title: null,
        items: [{ label: 'Print stickers' }],
      },
    ],
  },
  {
    key: 'Build (Pre-Electrical)',
    name: 'Build (Pre-Electrical)',
    sections: [
      {
        title: 'Front Section / First Fix wiring',
        items: [
          { label: 'Ducting' },
          { label: 'Fit all glands' },
          { label: 'First-fix wiring, including GuardIAN cables' },
          { label: 'Front wall and door stripe to be applied' },
          { label: 'Door seal installation' },
          { label: 'Port-hole window' },
          { label: '2 inch Vent valves' },
          { label: 'Strobe lights and siren' },
        ],
      },
      {
        title: 'Back Section',
        items: [
          { label: 'Back wall' },
          { label: 'Comms box' },
          { label: 'Iso switch' },
          {
            label:
              'Air-conditioning condenser external and evaporator install internal (Vac test and Gas release)',
          },
          { label: 'Battery box fitout' },
          { label: 'Cable looms' },
          { label: 'Probes' },
          { label: 'Floor mat' },
          { label: 'Battery box doors fit-out' },
        ],
      },
      {
        title: 'CAMS Installation',
        items: [
          { label: 'CAMS external install' },
          { label: 'CAMS internal install (including ECU and ECU wiring)' },
        ],
      },
      {
        title: 'Scrubber System',
        items: [
          { label: 'Install scrubber' },
          { label: 'Connect low-voltage wiring' },
        ],
      },
    ],
  },
  {
    key: 'Electrician Testing',
    name: 'Electrician Testing',
    sections: [
      {
        title: 'Electrical Work',
        items: [
          { label: 'Electrician to complete all electrical works on the chamber' },
          { label: 'Produce electrical certificate' },
        ],
      },
    ],
  },
  {
    key: 'Build (Post-Electrical)',
    name: 'Build (Post-Electrical)',
    sections: [
      {
        title: 'Interior Fit-Out',
        items: [
          { label: 'Seats' },
          { label: 'Floor mat' },
          { label: 'Toilet curtain' },
          { label: 'Gas cylinder brackets' },
          { label: 'Other internal accessories' },
        ],
      },
      {
        title: 'External / Internal Finishing',
        items: [
          { label: 'Operational stickers' },
          { label: 'External green stripe' },
          { label: 'Final external and internal markings' },
        ],
      },
      {
        title: 'Commissioning',
        items: [
          { label: 'Load chemicals and consumables' },
          { label: 'Clean and tidy chamber' },
          { label: 'Set parameters on scrubber' },
          { label: 'Carry out vacuum test on chamber' },
        ],
      },
    ],
  },
  {
    key: 'Approvals',
    name: 'Approvals',
    sections: [
      {
        title: null,
        items: [
          { label: 'Approval Check list' },
          { label: 'Two Supervisors from workshop' },
          { label: 'Sales BDM sign off' },
        ],
      },
    ],
  },
  {
    key: 'Shipping',
    name: 'Shipping',
    sections: [
      {
        title: null,
        items: [{ label: 'Despatch' }, { label: 'Shipping photos' }],
      },
    ],
  },
];

const STAGE_ORDER = PRODUCTION_STRUCTURE.map((s) => s.name);

const stageColors = {
  'Pre-build': 'default',
  Fabrication: 'blue',
  'Paint & Blast': 'purple',
  Materials: 'gold',
  Stickers: 'cyan',
  'Build (Pre-Electrical)': 'lime',
  'Electrician Testing': 'orange',
  'Build (Post-Electrical)': 'geekblue',
  Approvals: 'green',
  Shipping: 'volcano',
};

// ---------- HELPERS ----------

function enrichStructureWithStatus(currentStageIndex, product) {
  const defaultCompletedBy = product.businessManager || 'Workshop Supervisor';
  const defaultCompletedAt = '2025-06-01 10:15';

  return PRODUCTION_STRUCTURE.map((stage, stageIndex) => {
    const sections = stage.sections.map((section) => {
      const items = section.items.map((item, itemIndex) => {
        let status = 'todo';

        if (stageIndex < currentStageIndex) {
          status = 'done';
        } else if (stageIndex === currentStageIndex) {
          status = itemIndex < 2 ? 'done' : 'in-progress';
        } else {
          status = 'todo';
        }

        return {
          ...item,
          status,
          completedBy: status === 'done' ? defaultCompletedBy : null,
          completedAt: status === 'done' ? defaultCompletedAt : null,
        };
      });

      return { ...section, items };
    });

    return { ...stage, sections };
  });
}

function computeStageProgress(enrichedStage) {
  const allTasks = enrichedStage.sections.flatMap((s) => s.items);
  if (!allTasks.length) return 0;
  const doneCount = allTasks.filter((t) => t.status === 'done').length;
  return Math.round((doneCount / allTasks.length) * 100);
}

// ---------- COMPONENT ----------

const ProductionTimelineView = () => {
  const location = useLocation();
  const history = useHistory();
  const { product } = location.state || {};

  if (!product) {
    return (
      <SubPageLayout title="Product Timeline">
        <p>No product selected. Please navigate from the Production Timeline.</p>
      </SubPageLayout>
    );
  }

  const currentStageIndexRaw = STAGE_ORDER.indexOf(product.currentStage);
  const currentStageIndex =
    currentStageIndexRaw >= 0 ? currentStageIndexRaw : 0;

  const enrichedStages = useMemo(
    () => enrichStructureWithStatus(currentStageIndex, product),
    [currentStageIndex, product]
  );

  const [activeStageKeys, setActiveStageKeys] = useState([
    enrichedStages[currentStageIndex]?.key,
  ]);

  return (
    <SubPageLayout
      title="Product Timeline"
      extra={
        <Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()}>
          Back to Production Timeline
        </Button>
      }
    >
      <Row gutter={24}>
        {/* LEFT COLUMN: product, customer, detailed stage breakdown */}
        <Col xs={24} lg={10}>
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
              marginBottom: 24,
            }}
          >
            <Row gutter={16}>
              <Col span={10}>
                <div
                  style={{
                    width: '100%',
                    paddingTop: '100%',
                    position: 'relative',
                    borderRadius: 16,
                    overflow: 'hidden',
                    background:
                      'radial-gradient(circle at 0% 0%, #e5f0ff, #eff6ff 50%, #f9fafb)',
                  }}
                >
                  <img
                    src={product.image}
                    alt={product.productName}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </Col>
              <Col span={14}>
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    {product.productName}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: '#6b7280',
                      marginTop: 2,
                    }}
                  >
                    {product.productCode}
                  </div>
                </div>

                <Space direction="vertical" size={4}>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ color: '#6b7280' }}>Serial:&nbsp;</span>
                    <strong>{product.serialNumber}</strong>
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ color: '#6b7280' }}>MAFQ #:&nbsp;</span>
                    <strong>{product.mafqNumber}</strong>
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ color: '#6b7280' }}>Due Date:&nbsp;</span>
                    <strong>{product.dueDate}</strong>
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ color: '#6b7280' }}>
                      Business Manager:&nbsp;
                    </span>
                    <strong>{product.businessManager}</strong>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <span
                      style={{ color: '#6b7280', fontSize: 12, marginRight: 6 }}
                    >
                      Current Stage:
                    </span>
                    <Tag
                      color={stageColors[product.currentStage] || 'default'}
                    >
                      {product.currentStage}
                    </Tag>
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>

          <Card
            title="Customer & Sales Information"
            bordered={false}
            style={{ borderRadius: 16, marginBottom: 24 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Customer">
                {product.companyName}
              </Descriptions.Item>
              <Descriptions.Item label="Site Name">
                {product.siteName}
              </Descriptions.Item>
              <Descriptions.Item label="Country">
                {product.country}
              </Descriptions.Item>
              <Descriptions.Item label="Industry">
                {product.industry}
              </Descriptions.Item>
              <Descriptions.Item label="Fabricator">
                {product.fabricator || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Paint & Blast Contractor">
                {product.paintBlast || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* NEW: detailed production stage breakdown on the left */}
          <Card
            title="Production Stages Details"
            bordered={false}
            style={{
              borderRadius: 16,
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.06)',
            }}
          >
            <Collapse
              activeKey={activeStageKeys}
              onChange={(keys) =>
                setActiveStageKeys(Array.isArray(keys) ? keys : [keys])
              }
              bordered={false}
            >
              {enrichedStages.map((stage) => {
                const progress = computeStageProgress(stage);

                return (
                  <Panel
                    header={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>{stage.name}</span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          {progress}% complete
                        </span>
                      </div>
                    }
                    key={stage.key}
                  >
                    {stage.sections.map((section) => (
                      <div
                        key={section.title || 'root'}
                        style={{ marginBottom: 12 }}
                      >
                        {section.title && (
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#111827',
                              marginBottom: 4,
                            }}
                          >
                            {section.title}
                          </div>
                        )}

                        <ul
                          style={{
                            paddingLeft: 18,
                            marginBottom: 0,
                          }}
                        >
                          {section.items.map((task) => (
                            <li
                              key={task.label}
                              style={{
                                marginBottom: 4,
                                fontSize: 13,
                                color:
                                  task.status === 'done'
                                    ? '#065f46'
                                    : task.status === 'in-progress'
                                    ? '#1d4ed8'
                                    : '#4b5563',
                              }}
                            >
                              <span>{task.label}</span>
                              {task.status === 'done' && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: '#6b7280',
                                    marginLeft: 8,
                                  }}
                                >
                                  Completed by{' '}
                                  <strong>{task.completedBy}</strong> at{' '}
                                  {task.completedAt}
                                </span>
                              )}
                              {task.status === 'in-progress' && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: '#6b7280',
                                    marginLeft: 8,
                                  }}
                                >
                                  In progress&hellip;
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </Panel>
                );
              })}
            </Collapse>
          </Card>
        </Col>

        {/* RIGHT COLUMN: stage spine & quick overview */}
        <Col xs={24} lg={14}>
          <Card
            title="Production Stages"
            bordered={false}
            style={{
              borderRadius: 16,
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.06)',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                marginBottom: 12,
                fontSize: 13,
                color: '#6b7280',
              }}
            >
              This unit moves through <strong>10 production stages</strong>.
              The progress bar below shows how far the product is in each
              stage. Click a stage name to expand its detailed tasks in the
              “Production Stages Details” card on the left.
            </div>

            <Steps current={currentStageIndex} direction="vertical">
              {enrichedStages.map((stage, index) => {
                const progress = computeStageProgress(stage);
                const isCurrent = index === currentStageIndex;
                const isDone = index < currentStageIndex;

                return (
                  <Step
                    key={stage.key}
                    title={
                      <span
                        style={{
                          fontWeight: isCurrent ? 600 : 500,
                          color: isDone
                            ? '#16a34a'
                            : isCurrent
                            ? '#1d4ed8'
                            : '#374151',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          setActiveStageKeys((prev) =>
                            prev.includes(stage.key)
                              ? prev.filter((k) => k !== stage.key)
                              : [...prev, stage.key]
                          );
                        }}
                      >
                        {stage.name}
                      </span>
                    }
                    status={isDone ? 'finish' : isCurrent ? 'process' : 'wait'}
                    description={
                      <div style={{ marginTop: 4 }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                            fontSize: 11,
                            color: '#6b7280',
                          }}
                        >
                          <span>Tasks complete</span>
                          <span>{progress}%</span>
                        </div>
                        <Progress
                          percent={progress}
                          size="small"
                          showInfo={false}
                        />
                      </div>
                    }
                  />
                );
              })}
            </Steps>
          </Card>

          <Card
            title="Quick Overview"
            bordered={false}
            style={{ borderRadius: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>MAFQ #</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {product.mafqNumber}
                </div>
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Serial</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {product.serialNumber}
                </div>
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Due Date</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {product.dueDate}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </SubPageLayout>
  );
};

export default ProductionTimelineView;
